import styled from 'styled-components';

import { H2, H3, H4 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';

export const PageShell = styled.div`
  box-sizing: border-box;
  min-width: 0;
  width: min(100%, 930px);
`;

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  strong {
    font-weight: 700;
  }

  @media (max-width: 639px) {
    gap: 10px;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    text-align: center;

    > span {
      width: 100%;
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
`;

export const FormCard = styled.div<{ isTablet: boolean }>`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: ${(p) => (p.isTablet ? '30px 18px' : '36px 52px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
`;

export const SectionTitle = styled(H4)`
  margin: 0 0 18px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 14px 28px;
  margin-bottom: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

export const InfoLabel = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const InfoValue = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
`;

export const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 38px;
`;

export const OtpControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const OtpFieldRow = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 134px;
  gap: 16px;
  align-items: end;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const EmailFieldRow = styled.div`
  width: 100%;
`;

export const EmailOtpActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

export const OtpStatusText = styled(Text)<{ tone: 'info' | 'success' | 'error' }>`
  margin: 0;
  color: ${(p) =>
    p.tone === 'success'
      ? p.theme.colors.secondary500
      : p.tone === 'error'
      ? p.theme.colors.danger
      : p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;

export const ConsentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 28px;

  a {
    color: ${(p) => p.theme.colors.secondary500};
    text-decoration: underline;
  }

  button {
    padding: 0;
    border: 0;
    background: transparent;
    color: ${(p) => p.theme.colors.secondary500};
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
  }

  .MuiFormControlLabel-root {
    align-items: flex-start;
    min-width: 0;
    margin: 0;
  }

  .MuiFormControlLabel-label {
    min-width: 0;
  }

  .MuiCheckbox-root {
    flex: 0 0 auto;
    padding: 4px 12px 4px 0;
  }
`;

export const ConsentText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
`;

export const SignatureBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin-bottom: 32px;
`;

export const SignatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SignatureRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: ${(p) => (p.isMobile ? '8px' : '14px')};
  align-items: center;
  flex-wrap: wrap;
`;

export const DateText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const SignedBadge = styled(Text).attrs({ as: 'span' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 106px;
  height: 40px;
  margin: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.secondary500};
  color: ${(p) => p.theme.colors.light};
  font-weight: 700;
`;

export const SignaturePreview = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-left: 4px;
`;

export const SignaturePreviewImage = styled.img`
  display: block;
  width: min(220px, 100%);
  max-height: 88px;
  object-fit: contain;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  background: ${(p) => p.theme.colors.light};
`;

export const SignaturePreviewAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primary300};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

export const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  gap: 16px;

  @media (max-width: 639px) {
    width: 100%;
    min-width: 0;
    flex-direction: column;

    > button {
      width: 100%;
      min-width: 0;
    }
  }
`;

export const ModalSurface = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(530px, calc(100vw - 32px));
  padding: 24px 26px 22px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
  transform: translate(-50%, -50%);
  box-shadow: ${(p) => p.theme.shadows.base};
`;

export const ModalTitle = styled(H3)`
  margin: 0 0 22px;
  font-weight: 700;
`;

export const ModalText = styled(Text)`
  margin: 0 0 18px;
  line-height: 1.35;
`;

export const ModalAction = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 28px;
`;

export const AuthorizationModalSurface = styled(ModalSurface)`
  width: min(940px, calc(100vw - 32px));
  padding: 42px 52px 40px;
  border-radius: 40px 40px 40px 0;

  @media (max-width: 768px) {
    max-height: calc(100vh - 32px);
    padding: 32px 24px 28px;
    overflow-y: auto;
  }
`;

export const AuthorizationModalTitle = styled(H2)`
  margin: 0 0 42px;
  font-weight: 700;
`;

export const AuthorizationModalText = styled(ModalText)`
  color: ${(p) => p.theme.colors.gray400};
`;

export const AuthorizationModalAction = styled(ModalAction)`
  gap: 16px;
  margin-top: 42px;

  @media (max-width: 639px) {
    flex-direction: column;

    > button {
      width: 100%;
      min-width: 0;
    }
  }
`;
